import { useState, useEffect, useMemo } from 'react';
import { subscriptionsAPI } from '../utils/api';
import { formatCurrency, formatDate, getStatusBadgeClass } from '../utils/formatters';
import Loading from '../components/Loading';
import ErrorMessage from '../components/ErrorMessage';
import Dropdown from '../components/Dropdown';
import { FiSearch, FiEdit2, FiX, FiRotateCcw } from 'react-icons/fi';

// Color palette for sources
const SOURCE_COLORS = [
  '#0ea5e9', // sky blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#f97316', // orange
  '#6366f1', // indigo
];

// Generate consistent color for a source name
const getSourceColor = (source) => {
  if (!source || source === 'Unknown') return '#9ca3af'; // gray for unknown
  let hash = 0;
  for (let i = 0; i < source.length; i++) {
    hash = source.charCodeAt(i) + ((hash << 5) - hash);
  }
  return SOURCE_COLORS[Math.abs(hash) % SOURCE_COLORS.length];
};

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState([]); // Multi-select array
  const [filterSource, setFilterSource] = useState([]); // Multi-select array
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Source edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [newSource, setNewSource] = useState('');
  const [saving, setSaving] = useState(false);

  // Subscription detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await subscriptionsAPI.getAll();
      setSubscriptions(response.data.data);
      setFilteredSubscriptions(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (subscription) => {
    setEditingSubscription(subscription);
    setNewSource(subscription.source || '');
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingSubscription(null);
    setNewSource('');
  };

  const handleSaveSource = async () => {
    if (!editingSubscription || !newSource.trim()) return;

    try {
      setSaving(true);
      await subscriptionsAPI.updateSource(editingSubscription.id, newSource.trim());

      // Update local state
      setSubscriptions(prev => prev.map(sub =>
        sub.id === editingSubscription.id
          ? { ...sub, source: newSource.trim(), sourceOverridden: true, sourceOverriddenAt: Date.now() / 1000 }
          : sub
      ));

      // Also update selectedSubscription if the detail modal is showing this subscription
      if (selectedSubscription && selectedSubscription.id === editingSubscription.id) {
        setSelectedSubscription(prev => ({
          ...prev,
          source: {
            ...prev.source,
            effective: newSource.trim(),
            overridden: true,
            overriddenAt: Date.now() / 1000
          }
        }));
      }

      closeEditModal();
    } catch (err) {
      alert(err.response?.data?.error?.message || 'Failed to update source');
    } finally {
      setSaving(false);
    }
  };

  const handleRevertSource = async () => {
    if (!editingSubscription) return;

    try {
      setSaving(true);
      const response = await subscriptionsAPI.clearSource(editingSubscription.id);
      const revertedSource = response.data.data.source;

      // Update local state with reverted source
      setSubscriptions(prev => prev.map(sub =>
        sub.id === editingSubscription.id
          ? { ...sub, source: revertedSource, sourceOverridden: false, sourceOverriddenAt: null }
          : sub
      ));

      // Also update selectedSubscription if the detail modal is showing this subscription
      if (selectedSubscription && selectedSubscription.id === editingSubscription.id) {
        setSelectedSubscription(prev => ({
          ...prev,
          source: {
            ...prev.source,
            effective: revertedSource,
            overridden: false,
            overriddenAt: null
          }
        }));
      }

      closeEditModal();
    } catch (err) {
      alert(err.response?.data?.error?.message || 'Failed to revert source');
    } finally {
      setSaving(false);
    }
  };

  // Detail modal handlers
  const handleRowClick = async (subscription, e) => {
    // Don't open detail modal if clicking on the source edit button
    if (e.target.closest('button')) return;

    try {
      setDetailLoading(true);
      setDetailError(null);
      setDetailModalOpen(true);

      const response = await subscriptionsAPI.getDetails(subscription.id);
      setSelectedSubscription(response.data.data);
    } catch (err) {
      setDetailError(err.response?.data?.error?.message || 'Failed to load subscription details');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedSubscription(null);
    setDetailError(null);
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  // Get unique sources for filter dropdown
  const uniqueSources = useMemo(() => {
    const sources = [...new Set(subscriptions.map(sub => sub.source || 'Unknown'))];
    return sources.sort((a, b) => {
      if (a === 'Unknown') return 1;
      if (b === 'Unknown') return -1;
      return a.localeCompare(b);
    });
  }, [subscriptions]);

  // Apply filters
  useEffect(() => {
    let filtered = subscriptions;

    // Filter by status (multi-select)
    if (filterStatus.length > 0) {
      filtered = filtered.filter(sub => filterStatus.includes(sub.status));
    }

    // Filter by source (multi-select)
    if (filterSource.length > 0) {
      filtered = filtered.filter(sub => filterSource.includes(sub.source || 'Unknown'));
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(sub =>
        sub.customer?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredSubscriptions(filtered);
  }, [subscriptions, filterStatus, filterSource, searchTerm]);

  // Reset to page 1 when filters change (must be before any conditional returns)
  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterSource, searchTerm, itemsPerPage]);

  // Pagination logic (calculated before render, used after)
  const totalPages = Math.ceil(filteredSubscriptions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSubscriptions = filteredSubscriptions.slice(startIndex, endIndex);

  const statusCounts = {
    all: subscriptions.length,
    active: subscriptions.filter(s => s.status === 'active').length,
    trialing: subscriptions.filter(s => s.status === 'trialing').length,
    canceled: subscriptions.filter(s => s.status === 'canceled').length,
    past_due: subscriptions.filter(s => s.status === 'past_due').length
  };

  if (loading) return <Loading message="Loading subscriptions..." />;
  if (error) return <ErrorMessage message={error} onRetry={fetchSubscriptions} />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Subscriptions</h1>
        <p className="mt-1 text-gray-600">
          Manage and view all subscription data
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card">
          <p className="text-sm text-gray-600">Total</p>
          <p className="text-2xl font-bold text-gray-900">{statusCounts.all}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Active</p>
          <p className="text-2xl font-bold text-green-600">{statusCounts.active}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Trialing</p>
          <p className="text-2xl font-bold text-blue-600">{statusCounts.trialing}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Canceled</p>
          <p className="text-2xl font-bold text-red-600">{statusCounts.canceled}</p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-600">Past Due</p>
          <p className="text-2xl font-bold text-yellow-600">{statusCounts.past_due}</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by email, name, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Status Filter */}
          <Dropdown
            value={filterStatus}
            onChange={setFilterStatus}
            multiple={true}
            placeholder="All Status"
            options={[
              { value: 'active', label: 'Active' },
              { value: 'trialing', label: 'Trialing' },
              { value: 'canceled', label: 'Canceled' },
              { value: 'past_due', label: 'Past Due' }
            ]}
            className="w-44"
          />

          {/* Source Filter */}
          <Dropdown
            value={filterSource}
            onChange={setFilterSource}
            multiple={true}
            placeholder="All Sources"
            options={uniqueSources.map(source => ({ value: source, label: source }))}
            className="w-48"
            renderOption={(opt) => (
              <div className="flex items-center gap-2">
                <div 
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: getSourceColor(typeof opt === 'object' ? opt.value : opt) }}
                />
                <span>{typeof opt === 'object' ? opt.label : opt}</span>
              </div>
            )}
          />
        </div>
      </div>

      {/* Subscriptions Table */}
      <div className="card">
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Status</th>
                <th>Amount</th>
                <th>Source</th>
                <th>Created</th>
                <th>Current Period</th>
              </tr>
            </thead>
            <tbody>
              {paginatedSubscriptions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500">
                    No subscriptions found
                  </td>
                </tr>
              ) : (
                paginatedSubscriptions.map((sub) => (
                  <tr
                    key={sub.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={(e) => handleRowClick(sub, e)}
                  >
                    <td>
                      <div>
                        <p className="font-medium text-gray-900">
                          {sub.customer?.name || 'N/A'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {sub.customer?.email || 'N/A'}
                        </p>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(sub.status)}`}>
                        {sub.status}
                      </span>
                    </td>
                    <td className="font-medium">
                      {formatCurrency(sub.plan?.amount, sub.plan?.currency)}
                      {sub.plan?.interval && (
                        <span className="text-xs text-gray-500">
                          /{sub.plan.interval}
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => openEditModal(sub)}
                        className="flex items-center gap-2 group hover:bg-gray-100 rounded px-2 py-1 -mx-2 -my-1 transition-colors"
                      >
                        <div
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: getSourceColor(sub.source) }}
                        />
                        <span className={`text-sm ${sub.source === 'Unknown' ? 'text-gray-400' : 'text-gray-900 font-medium'}`}>
                          {sub.source || 'Unknown'}
                        </span>
                        {sub.sourceOverridden && (
                          <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                            edited
                          </span>
                        )}
                        <FiEdit2 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    </td>
                    <td className="text-sm text-gray-600">
                      {formatDate(sub.created)}
                    </td>
                    <td className="text-sm text-gray-600">
                      {sub.status === 'canceled' && sub.canceledAt
                        ? `Canceled: ${formatDate(sub.canceledAt)}`
                        : `${formatDate(sub.currentPeriodStart)} - ${formatDate(sub.currentPeriodEnd)}`
                      }
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {filteredSubscriptions.length > 0 && (
        <div className="card">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Items per page selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show:</span>
              <Dropdown
                value={itemsPerPage}
                onChange={(val) => setItemsPerPage(Number(val))}
                options={[
                  { value: 25, label: '25' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' }
                ]}
                className="w-20"
              />
              <span className="text-sm text-gray-600">per page</span>
            </div>

            {/* Page info */}
            <div className="text-sm text-gray-600">
              Showing {startIndex + 1} to {Math.min(endIndex, filteredSubscriptions.length)} of {filteredSubscriptions.length} subscriptions
            </div>

            {/* Page navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Source Modal */}
      {editModalOpen && editingSubscription && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
          onClick={(e) => { if (e.target === e.currentTarget) closeEditModal(); }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Edit Source</h3>
              <button
                onClick={closeEditModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 space-y-4">
              {/* Subscription Info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600">Subscription</p>
                <p className="font-medium text-gray-900">{editingSubscription.customer?.email || 'N/A'}</p>
                <p className="text-xs text-gray-500 mt-1">{editingSubscription.id}</p>
              </div>

              {/* Original Source */}
              {editingSubscription.sourceOverridden && editingSubscription.sourceOriginal && (
                <div className="text-sm">
                  <span className="text-gray-600">Original source: </span>
                  <span className="font-medium text-gray-900">{editingSubscription.sourceOriginal}</span>
                </div>
              )}

              {/* Source Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source (Salesperson)
                </label>
                <input
                  type="text"
                  value={newSource}
                  onChange={(e) => setNewSource(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && newSource.trim()) handleSaveSource(); }}
                  className="input"
                  placeholder="Enter salesperson name"
                  autoFocus
                />
              </div>

              {/* Quick Select from existing sources */}
              {uniqueSources.filter(s => s !== 'Unknown').length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quick select from existing sources
                  </label>
                  <Dropdown
                    value=""
                    onChange={(val) => { if (val) setNewSource(val); }}
                    options={uniqueSources.filter(s => s !== 'Unknown').map(source => ({ value: source, label: source }))}
                    placeholder="Search existing sources..."
                    searchable={true}
                    className="w-full"
                    renderOption={(opt) => (
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: getSourceColor(typeof opt === 'object' ? opt.value : opt) }}
                        />
                        <span>{typeof opt === 'object' ? opt.label : opt}</span>
                      </div>
                    )}
                  />
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50 rounded-b-lg">
              <div>
                {editingSubscription.sourceOverridden && (
                  <button
                    onClick={handleRevertSource}
                    disabled={saving}
                    className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800"
                  >
                    <FiRotateCcw className="w-4 h-4" />
                    Revert to original
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={closeEditModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSource}
                  disabled={saving || !newSource.trim()}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Detail Modal */}
      {detailModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => { if (e.target === e.currentTarget) closeDetailModal(); }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Subscription Details</h3>
              <button
                onClick={closeDetailModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 overflow-y-auto flex-1">
              {detailLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : detailError ? (
                <div className="text-center py-12">
                  <p className="text-red-600">{detailError}</p>
                  <button
                    onClick={closeDetailModal}
                    className="mt-4 text-sm text-gray-600 hover:text-gray-800"
                  >
                    Close
                  </button>
                </div>
              ) : selectedSubscription ? (
                <div className="space-y-4">
                  {/* Customer Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Customer</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Name</p>
                        <p className="font-medium text-gray-900">{selectedSubscription.customer?.name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Email</p>
                        <p className="font-medium text-gray-900">{selectedSubscription.customer?.email || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">Customer ID</p>
                      <p className="text-sm font-mono text-gray-600">{selectedSubscription.customer?.id || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Subscription Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Subscription</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Status</p>
                        <span className={`badge ${getStatusBadgeClass(selectedSubscription.status)}`}>
                          {selectedSubscription.status}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Amount</p>
                        <p className="font-medium text-gray-900">
                          {formatCurrency(selectedSubscription.plan?.amount, selectedSubscription.plan?.currency)}
                          <span className="text-xs text-gray-500">/{selectedSubscription.plan?.interval}</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Created</p>
                        <p className="font-medium text-gray-900">{formatDate(selectedSubscription.created)}</p>
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">Subscription ID</p>
                      <p className="text-sm font-mono text-gray-600">{selectedSubscription.id}</p>
                    </div>
                  </div>

                  {/* Current Period */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Current Period</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Period</p>
                        <p className="font-medium text-gray-900">
                          {formatDate(selectedSubscription.currentPeriodStart)} - {formatDate(selectedSubscription.currentPeriodEnd)}
                        </p>
                      </div>
                      {selectedSubscription.canceledAt && (
                        <div>
                          <p className="text-xs text-gray-500">Canceled At</p>
                          <p className="font-medium text-red-600">{formatDate(selectedSubscription.canceledAt)}</p>
                        </div>
                      )}
                      {selectedSubscription.cancelAtPeriodEnd && !selectedSubscription.canceledAt && (
                        <div>
                          <p className="text-xs text-gray-500">Cancellation</p>
                          <p className="font-medium text-amber-600">Cancels at period end</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Source */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Source / Salesperson</h4>
                    <button
                      onClick={() => {
                        // Create a subscription object compatible with openEditModal
                        const subForEdit = {
                          id: selectedSubscription.id,
                          customer: selectedSubscription.customer,
                          source: selectedSubscription.source?.effective,
                          sourceOverridden: selectedSubscription.source?.overridden,
                          sourceOriginal: selectedSubscription.source?.original
                        };
                        openEditModal(subForEdit);
                      }}
                      className="flex items-center gap-2 group hover:bg-gray-100 rounded px-2 py-1 -mx-2 -my-1 transition-colors w-full text-left"
                    >
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: getSourceColor(selectedSubscription.source?.effective) }}
                      />
                      <span className="font-medium text-gray-900">
                        {selectedSubscription.source?.effective || 'Unknown'}
                      </span>
                      {selectedSubscription.source?.overridden && (
                        <span className="text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">edited</span>
                      )}
                      <FiEdit2 className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity ml-auto" />
                    </button>
                    {selectedSubscription.source?.overridden && selectedSubscription.source?.original && (
                      <p className="text-xs text-gray-500 mt-1">
                        Original: {selectedSubscription.source.original}
                      </p>
                    )}
                  </div>

                  {/* Payments (Invoices) */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">
                      Payments ({selectedSubscription.invoices?.length || 0})
                    </h4>
                    {selectedSubscription.invoices?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-gray-500 border-b">
                              <th className="pb-2 font-medium">Invoice</th>
                              <th className="pb-2 font-medium">Status</th>
                              <th className="pb-2 font-medium">Amount</th>
                              <th className="pb-2 font-medium">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedSubscription.invoices.map((invoice) => (
                              <tr key={invoice.id} className="border-b border-gray-100 last:border-0">
                                <td className="py-2 font-mono text-xs">{invoice.number || invoice.id?.slice(-8)}</td>
                                <td className="py-2">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                                    invoice.status === 'open' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {invoice.status}
                                  </span>
                                </td>
                                <td className="py-2 font-medium">{formatCurrency(invoice.amountPaid, invoice.currency)}</td>
                                <td className="py-2 text-gray-600">{invoice.paidAt ? formatDate(invoice.paidAt) : 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No payments found</p>
                    )}
                  </div>

                  {/* Refunds */}
                  {selectedSubscription.refunds?.length > 0 && (
                    <div className="bg-red-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-red-700 mb-2">
                        Refunds ({selectedSubscription.refunds.length})
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="text-left text-xs text-red-600 border-b border-red-100">
                              <th className="pb-2 font-medium">Amount</th>
                              <th className="pb-2 font-medium">Status</th>
                              <th className="pb-2 font-medium">Reason</th>
                              <th className="pb-2 font-medium">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedSubscription.refunds.map((refund) => (
                              <tr key={refund.id} className="border-b border-red-100 last:border-0">
                                <td className="py-2 font-medium text-red-700">-{formatCurrency(refund.amount, refund.currency)}</td>
                                <td className="py-2">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                    {refund.status}
                                  </span>
                                </td>
                                <td className="py-2 text-gray-600 capitalize">{refund.reason?.replace(/_/g, ' ') || 'N/A'}</td>
                                <td className="py-2 text-gray-600">{refund.created ? formatDate(refund.created) : 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Summary */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-700 mb-2">Summary</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-blue-600">Total Paid</p>
                        <p className="font-semibold text-blue-900">
                          {formatCurrency(selectedSubscription.stats?.totalPaid, selectedSubscription.plan?.currency)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-600">Total Refunded</p>
                        <p className="font-semibold text-red-600">
                          {selectedSubscription.stats?.totalRefunded > 0 ? '-' : ''}
                          {formatCurrency(selectedSubscription.stats?.totalRefunded, selectedSubscription.plan?.currency)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-blue-600">Net Revenue</p>
                        <p className="font-semibold text-green-600">
                          {formatCurrency(selectedSubscription.stats?.netRevenue, selectedSubscription.plan?.currency)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end px-6 py-4 border-t bg-gray-50 rounded-b-lg flex-shrink-0">
              <button
                onClick={closeDetailModal}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;
